import { deserializeDrawings, exportDrawingsJSON, importDrawingsJSON } from "./serialization";
import { DRAWINGS_KEY_PREFIX, drawingsAllKey, drawingsKey } from "./storageKeys";
export const DRAWING_TOOL_TYPES = [
    "trendLine",
    "hLine",
    "vLine",
    "fibonacci",
    "channel",
    "text",
    "rectangle",
    "arrow",
    "ray",
    "extendedLine",
    "polyline",
    "dateAndPriceRange",
    "longPosition",
    "shortPosition",
    "fibExtension",
    "parallelChannel",
    "pitchfork",
    "abcdPattern",
    "fibArc",
    "fibTimeZone",
    "regressionChannel",
    "gannFan",
    "elliottImpulse",
    "elliottCorrection",
    "elliottTriangle",
    "elliottDoubleCombo",
    "elliottTripleCombo",
    "xabcdPattern",
    "cypherPattern",
    "headAndShoulders",
    "trianglePattern",
    "threeDrivesPattern",
    // DT-13: Extended Fibonacci
    "fibChannel",
    "fibSpeedResistanceFan",
    "trendBasedFibTime",
    "fibCircles",
    "fibSpiral",
    "fibSpeedResistanceArcs",
    "fibWedge",
    "pitchfan",
    // DT-14: Gann Advanced
    "gannBox",
    "gannSquareFixed",
    "gannSquare",
    // DT-15: Cycle Tools
    "cyclicLines",
    "timeCycles",
    "sineLine",
    // CE22: Drawing Tools Pack 1
    "disjointChannel",
    "flatTopBottom",
    "schiffPitchfork",
    "modifiedSchiff",
    "insidePitchfork",
    "gannFixed",
    "trendAngle",
    "infoLine",
    // CE24: Drawing Tools Pack 3
    "anchoredVwap",
    "iconMarker",
    "ghostFeed",
    "barsPattern",
    "crossLine",
    "datePriceRange",
    // CE23: Drawing Tools Pack 2
    "brush",
    "highlighter",
    "eraser",
    "curve",
    "arc",
    "path",
    "polygon",
    "rotatedRectangle",
    // TDM-B: Advanced Shapes
    "circle",
    "ellipse",
    "triangle",
    "callout",
    "priceLabel",
    // TDM-C: Measurement Tools
    "priceRange",
    "dateRange",
    "barsCount",
];
export class DrawingImportError extends Error {
    constructor(message) {
        super(message);
        this.name = "DrawingImportError";
    }
}
/** S3.4/F-36: typed REST storage failure (e.g. timeout) — never silent. */
export class DrawingStorageError extends Error {
    constructor(message) {
        super(message);
        this.name = "DrawingStorageError";
    }
}
/** S3.4/F-36: fetch with timeout (default 8s). Abort => DrawingStorageError. */
export async function fetchWithStorageTimeout(url, init, endpoint, timeoutMs = 8000) {
    const controller = new AbortController();
    const timeoutId = globalThis.setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { ...init, signal: controller.signal });
    }
    catch (error) {
        if (error?.name === "AbortError") {
            throw new DrawingStorageError(`REST drawings ${endpoint} timed out after ${timeoutMs}ms`);
        }
        throw error;
    }
    finally {
        globalThis.clearTimeout(timeoutId);
    }
}
function getStorage() {
    if (typeof globalThis === "undefined" || typeof globalThis.localStorage === "undefined") {
        throw new Error("localStorage is not available in this environment");
    }
    return globalThis.localStorage;
}
function isRecord(value) {
    return typeof value === "object" && value !== null;
}
function isPoint(value) {
    if (!isRecord(value) || typeof value.x !== "number" || !Number.isFinite(value.x) || typeof value.y !== "number" || !Number.isFinite(value.y)) {
        return false;
    }
    if (typeof value.xIndexFloat !== "undefined" && (typeof value.xIndexFloat !== "number" || !Number.isFinite(value.xIndexFloat))) {
        return false;
    }
    if (typeof value.timeMsHint !== "undefined" && (typeof value.timeMsHint !== "number" || !Number.isFinite(value.timeMsHint))) {
        return false;
    }
    if (typeof value.yPrice !== "undefined" && (typeof value.yPrice !== "number" || !Number.isFinite(value.yPrice))) {
        return false;
    }
    return true;
}
function cloneNormalizedPoint(point) {
    return {
        x: point.x,
        y: point.y,
        ...(typeof point.xIndexFloat === "number" && Number.isFinite(point.xIndexFloat) ? { xIndexFloat: point.xIndexFloat } : {}),
        ...(typeof point.timeMsHint === "number" && Number.isFinite(point.timeMsHint) ? { timeMsHint: point.timeMsHint } : {}),
        ...(typeof point.yPrice === "number" && Number.isFinite(point.yPrice) ? { yPrice: point.yPrice } : {}),
    };
}
function isStyle(value) {
    return isRecord(value) && typeof value.stroke === "string" && typeof value.strokeWidth === "number";
}
function isFibLevelConfig(value) {
    return isRecord(value)
        && typeof value.ratio === "number" && Number.isFinite(value.ratio)
        && typeof value.visible === "boolean";
}
/** DT-12: Safely parse harmonic ratio config. */
function isHarmonicRatioConfig(value) {
    return isRecord(value) && Array.isArray(value.ratioLabels)
        && value.ratioLabels.every((r) => typeof r === "string");
}
/** DT-15: Safely parse cycle params. */
function isCycleParams(value) {
    return isRecord(value) && typeof value.count === "number" && Number.isFinite(value.count);
}
/** DT-10-T10: Safely parse alert condition from deserialized JSON. */
function parseAlertCondition(value) {
    if (!isRecord(value))
        return undefined;
    const validDirections = ["crossAbove", "crossBelow", "either"];
    const direction = validDirections.includes(value.direction)
        ? value.direction
        : "either";
    const enabled = typeof value.enabled === "boolean" ? value.enabled : false;
    const cooldownMs = typeof value.cooldownMs === "number" && Number.isFinite(value.cooldownMs) && value.cooldownMs > 0
        ? value.cooldownMs
        : undefined;
    const triggeredAt = typeof value.triggeredAt === "number" && Number.isFinite(value.triggeredAt)
        ? value.triggeredAt
        : undefined;
    return { direction, enabled, cooldownMs, triggeredAt };
}
/**
 * Migrate legacy `fibLevels: number[]` to `fibLevelConfigs: FibLevelConfig[]`.
 * Returns `fibLevelConfigs` if already present, converts from fibLevels otherwise.
 * Returns undefined when neither field exists.
 */
function migrateFibLevelConfigs(value) {
    if (Array.isArray(value.fibLevelConfigs)) {
        const parsed = value.fibLevelConfigs.filter(isFibLevelConfig);
        return parsed.length > 0 ? parsed : undefined;
    }
    if (Array.isArray(value.fibLevels)) {
        const converted = value.fibLevels
            .filter((item) => typeof item === "number" && Number.isFinite(item))
            .map((ratio) => ({ ratio, visible: true }));
        return converted.length > 0 ? converted : undefined;
    }
    return undefined;
}
function isRiskReward(value) {
    return isRecord(value)
        && typeof value.entry === "number" && Number.isFinite(value.entry)
        && typeof value.stop === "number" && Number.isFinite(value.stop)
        && typeof value.target === "number" && Number.isFinite(value.target)
        && (typeof value.quantity === "undefined" || (typeof value.quantity === "number" && Number.isFinite(value.quantity)));
}
function isSegmentStyles(value) {
    if (!isRecord(value) || Array.isArray(value))
        return false;
    return Object.values(value).every((v) => isRecord(v) && typeof v.stroke === "string");
}
function isSegmentVisible(value) {
    if (!isRecord(value) || Array.isArray(value))
        return false;
    return Object.values(value).every((v) => typeof v === "boolean");
}
function isVisibility(value) {
    if (!isRecord(value))
        return false;
    if (value.timeframes != null && !Array.isArray(value.timeframes))
        return false;
    if (value.symbols != null && !Array.isArray(value.symbols))
        return false;
    if (value.hidden != null && typeof value.hidden !== "boolean")
        return false;
    return true;
}
function normalizeDasharray(value) {
    if (typeof value !== "string") {
        return undefined;
    }
    const normalized = value.toLowerCase();
    if (normalized === "solid" || normalized === "dashed" || normalized === "dotted") {
        return normalized;
    }
    return undefined;
}
function isDrawingType(value) {
    return typeof value === "string" && DRAWING_TOOL_TYPES.includes(value);
}
export function normalizeDrawingObject(value, index) {
    if (!isRecord(value)) {
        throw new DrawingImportError(`Drawing[${index}] must be an object`);
    }
    if (typeof value.id !== "string" || value.id.length === 0) {
        throw new DrawingImportError(`Drawing[${index}].id must be a non-empty string`);
    }
    if (!isDrawingType(value.type)) {
        throw new DrawingImportError(`Drawing[${index}].type is invalid`);
    }
    if (!Array.isArray(value.points) || value.points.some((point) => !isPoint(point))) {
        throw new DrawingImportError(`Drawing[${index}].points must be an array of points`);
    }
    if (!isStyle(value.style)) {
        throw new DrawingImportError(`Drawing[${index}].style must include stroke and strokeWidth`);
    }
    const createdAt = typeof value.createdAt === "number" && Number.isFinite(value.createdAt) ? value.createdAt : Date.now();
    const updatedAt = typeof value.updatedAt === "number" && Number.isFinite(value.updatedAt) ? value.updatedAt : createdAt;
    const style = {
        stroke: value.style.stroke,
        strokeWidth: value.style.strokeWidth,
        strokeDasharray: normalizeDasharray(value.style.strokeDasharray),
        fill: typeof value.style.fill === "string" ? value.style.fill : undefined,
        fillOpacity: typeof value.style.fillOpacity === "number" && Number.isFinite(value.style.fillOpacity) ? value.style.fillOpacity : undefined,
        opacity: typeof value.style.opacity === "number" && Number.isFinite(value.style.opacity) ? value.style.opacity : undefined,
        fontSize: typeof value.style.fontSize === "number" && Number.isFinite(value.style.fontSize) ? value.style.fontSize : undefined,
        fontFamily: typeof value.style.fontFamily === "string" ? value.style.fontFamily : undefined,
        icon: typeof value.style.icon === "string" ? value.style.icon : undefined,
        textBoxWidth: typeof value.style.textBoxWidth === "number" && Number.isFinite(value.style.textBoxWidth) ? value.style.textBoxWidth : undefined,
        textBoxHeight: typeof value.style.textBoxHeight === "number" && Number.isFinite(value.style.textBoxHeight) ? value.style.textBoxHeight : undefined,
        // P1-06: previously dropped by the whitelist (read via `as any` at render/hit
        // sites) — losing them on every reload. Types added to DrawingStyle.
        size: typeof value.style.size === "number" && Number.isFinite(value.style.size) ? value.style.size : undefined,
        color: typeof value.style.color === "string" ? value.style.color : undefined,
        showLabels: typeof value.style.showLabels === "boolean" ? value.style.showLabels : undefined,
        bandMultiplier: typeof value.style.bandMultiplier === "number" && Number.isFinite(value.style.bandMultiplier) ? value.style.bandMultiplier : undefined,
        // Phase A (Notes): twin-repo typography/background — must whitelist or lost on reload
        backgroundColor: typeof value.style.backgroundColor === "string" ? value.style.backgroundColor : undefined,
        borderColor: typeof value.style.borderColor === "string" ? value.style.borderColor : undefined,
        borderRadius: typeof value.style.borderRadius === "number" && Number.isFinite(value.style.borderRadius) ? value.style.borderRadius : undefined,
        shadowBlur: typeof value.style.shadowBlur === "number" && Number.isFinite(value.style.shadowBlur) ? value.style.shadowBlur : undefined,
        bold: typeof value.style.bold === "boolean" ? value.style.bold : undefined,
        italic: typeof value.style.italic === "boolean" ? value.style.italic : undefined,
        textAlign: value.style.textAlign === "left" || value.style.textAlign === "center" || value.style.textAlign === "right" ? value.style.textAlign : undefined,
    };
    return {
        id: value.id,
        type: value.type,
        points: value.points.map(cloneNormalizedPoint),
        style,
        paneId: typeof value.paneId === "string" && value.paneId.length > 0 ? value.paneId : undefined,
        yScaleId: typeof value.yScaleId === "string" && value.yScaleId.length > 0 ? value.yScaleId : undefined,
        text: typeof value.text === "string" ? value.text : undefined,
        fibLevels: Array.isArray(value.fibLevels) ? value.fibLevels.filter((item) => typeof item === "number" && Number.isFinite(item)) : undefined,
        fibLevelConfigs: migrateFibLevelConfigs(value),
        label: typeof value.label === "string" ? value.label : undefined,
        symbol: typeof value.symbol === "string" ? value.symbol : undefined,
        timeframe: typeof value.timeframe === "string" ? value.timeframe : undefined,
        zIndex: typeof value.zIndex === "number" && Number.isFinite(value.zIndex) ? value.zIndex : undefined,
        clonedFrom: typeof value.clonedFrom === "string" ? value.clonedFrom : undefined,
        groupId: typeof value.groupId === "string" ? value.groupId : undefined,
        riskReward: isRiskReward(value.riskReward)
            ? {
                entry: value.riskReward.entry,
                stop: value.riskReward.stop,
                target: value.riskReward.target,
                quantity: value.riskReward.quantity,
            }
            : undefined,
        segmentStyles: isSegmentStyles(value.segmentStyles) ? value.segmentStyles : undefined,
        segmentVisible: isSegmentVisible(value.segmentVisible) ? value.segmentVisible : undefined,
        extendLeft: typeof value.extendLeft === "boolean" ? value.extendLeft : undefined,
        extendRight: typeof value.extendRight === "boolean" ? value.extendRight : undefined,
        locked: typeof value.locked === "boolean" ? value.locked : undefined,
        visible: typeof value.visible === "boolean" ? value.visible : undefined,
        hidden: typeof value.hidden === "boolean" ? value.hidden : undefined,
        // P1-06: measurement badge flag was never read back — trendLine/channel
        // lost their persistent measurement overlay on every reload.
        showMeasurement: typeof value.showMeasurement === "boolean" ? value.showMeasurement : undefined,
        visibility: isVisibility(value.visibility) ? value.visibility : undefined,
        alertCondition: parseAlertCondition(value.alertCondition),
        alertConditions: Array.isArray(value.alertConditions)
            ? value.alertConditions
                .map((c) => parseAlertCondition(c))
                .filter((c) => c != null)
            : undefined,
        waveLabels: Array.isArray(value.waveLabels)
            ? value.waveLabels.filter((item) => typeof item === "string")
            : undefined,
        harmonicRatios: isHarmonicRatioConfig(value.harmonicRatios) ? value.harmonicRatios : undefined,
        gannSquareSize: typeof value.gannSquareSize === "number" && Number.isFinite(value.gannSquareSize) ? value.gannSquareSize : undefined,
        cycleParams: isCycleParams(value.cycleParams) ? value.cycleParams : undefined,
        createdAt,
        updatedAt,
    };
}
function normalizeDrawings(payload) {
    if (!Array.isArray(payload)) {
        throw new DrawingImportError("Drawing payload must be an array");
    }
    return payload.map((item, index) => normalizeDrawingObject(item, index));
}
export function createLocalStorageAdapter(keyPrefix = DRAWINGS_KEY_PREFIX) {
    // P1-03: returns success so callers can surface quota/private-mode failures
    // instead of silently dropping the save. All errors are warned, not swallowed.
    function safeSetItem(key, value) {
        try {
            getStorage().setItem(key, value);
            return true;
        }
        catch (error) {
            if (error instanceof DOMException && error.name === "QuotaExceededError") {
                console.warn(`DrawingStorage quota exceeded for key "${key}". Try deleting old drawings.`);
            }
            else {
                console.warn(`DrawingStorage failed to persist key "${key}":`, error);
            }
            return false;
        }
    }
    function drawingsStorageKey(symbol, timeframe) {
        return drawingsKey(symbol, timeframe, { prefix: keyPrefix });
    }
    function symbolStorageKey(symbol) {
        return drawingsAllKey(symbol, { prefix: keyPrefix });
    }
    return {
        save(symbol, timeframe, drawings) {
            safeSetItem(drawingsStorageKey(symbol, timeframe), exportDrawingsJSON(drawings));
        },
        saveWithResult(symbol, timeframe, drawings) {
            return safeSetItem(drawingsStorageKey(symbol, timeframe), exportDrawingsJSON(drawings));
        },
        load(symbol, timeframe) {
            const raw = getStorage().getItem(drawingsStorageKey(symbol, timeframe));
            if (!raw) {
                return [];
            }
            try {
                // P1-01: deserializeDrawings now throws DrawingImportError on corrupt
                // JSON (previously swallowed to `[]`), so this branch actually fires
                // and the rotten key is cleared with a warning.
                return normalizeDrawings(deserializeDrawings(raw));
            }
            catch (error) {
                if (error instanceof DrawingImportError) {
                    getStorage().removeItem(drawingsStorageKey(symbol, timeframe));
                    console.warn(`Ignoring invalid drawing cache for ${symbol}/${timeframe}:`, error.message);
                    return [];
                }
                // S3.4/F-34: unexpected errors are logged loudly (never silent).
                console.error(`[DrawingStorage] load failed for ${symbol}/${timeframe}:`, error);
                return [];
            }
        },
        clear(symbol, timeframe) {
            getStorage().removeItem(drawingsStorageKey(symbol, timeframe));
        },
        exportJSON(drawings) {
            return exportDrawingsJSON(drawings);
        },
        importJSON(payload) {
            try {
                return normalizeDrawings(importDrawingsJSON(payload));
            }
            catch (error) {
                if (error instanceof DrawingImportError) {
                    throw error;
                }
                throw new DrawingImportError(error instanceof Error ? error.message : "Invalid drawing JSON payload");
            }
        },
        // P1/D-09: `saveAllForSymbol` removed (was a second writer clobbering the
        // per-timeframe source of truth). `loadAllForSymbol` stays as a legacy
        // read-cache for REST/backward compatibility; the editor never writes it.
        loadAllForSymbol(symbol) {
            const raw = getStorage().getItem(symbolStorageKey(symbol));
            if (!raw) {
                return [];
            }
            try {
                return normalizeDrawings(deserializeDrawings(raw));
            }
            catch (error) {
                if (error instanceof DrawingImportError) {
                    getStorage().removeItem(symbolStorageKey(symbol));
                    console.warn(`Ignoring invalid drawing cache for ${symbol} (all timeframes):`, error.message);
                    return [];
                }
                return [];
            }
        },
        clearAllForSymbol(symbol) {
            getStorage().removeItem(symbolStorageKey(symbol));
        },
        // Phase 4.1: ChartTemplate persistence
        saveTemplate(template) {
            const key = getTemplateStorageKey(template.id, keyPrefix);
            safeSetItem(key, serializeChartTemplate(template));
            // Update the index of all template IDs
            const index = loadTemplateIndex(keyPrefix);
            if (!index.includes(template.id)) {
                index.push(template.id);
                saveTemplateIndex(index, keyPrefix);
            }
        },
        loadTemplate(templateId) {
            const raw = getStorage().getItem(getTemplateStorageKey(templateId, keyPrefix));
            if (!raw)
                return null;
            try {
                return deserializeChartTemplate(raw);
            }
            catch {
                getStorage().removeItem(getTemplateStorageKey(templateId, keyPrefix));
                return null;
            }
        },
        listTemplates() {
            return loadTemplateIndex(keyPrefix);
        },
        deleteTemplate(templateId) {
            getStorage().removeItem(getTemplateStorageKey(templateId, keyPrefix));
            const index = loadTemplateIndex(keyPrefix);
            const next = index.filter((id) => id !== templateId);
            saveTemplateIndex(next, keyPrefix);
        },
    };
}
function getTemplateStorageKey(templateId, keyPrefix = DRAWINGS_KEY_PREFIX) {
    return `${keyPrefix}-template-${templateId}`;
}
function getTemplateIndexStorageKey(keyPrefix = DRAWINGS_KEY_PREFIX) {
    return `${keyPrefix}-templates-index`;
}
function loadTemplateIndex(keyPrefix = DRAWINGS_KEY_PREFIX) {
    const raw = getStorage().getItem(getTemplateIndexStorageKey(keyPrefix));
    if (!raw)
        return [];
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
            return parsed.filter((id) => typeof id === "string");
        }
        return [];
    }
    catch {
        return [];
    }
}
function saveTemplateIndex(index, keyPrefix = DRAWINGS_KEY_PREFIX) {
    safeSetItemLocal(getTemplateIndexStorageKey(keyPrefix), JSON.stringify(index));
}
function safeSetItemLocal(key, value) {
    try {
        getStorage().setItem(key, value);
    }
    catch {
        // ignore
    }
}
function serializeChartTemplate(template) {
    return JSON.stringify(template);
}
function deserializeChartTemplate(raw) {
    const parsed = JSON.parse(raw);
    if (!isRecord(parsed) || typeof parsed.id !== "string" || typeof parsed.name !== "string") {
        throw new DrawingImportError("ChartTemplate must have id and name");
    }
    if (!Array.isArray(parsed.drawings)) {
        throw new DrawingImportError("ChartTemplate.drawings must be an array");
    }
    const drawings = parsed.drawings.map((item, index) => normalizeDrawingObject(item, index));
    const indicators = Array.isArray(parsed.indicators)
        ? parsed.indicators.filter((i) => isRecord(i) && typeof i.name === "string")
        : [];
    const layout = isRecord(parsed.layout) && Array.isArray(parsed.layout.panes)
        ? {
            panes: parsed.layout.panes.filter((p) => isRecord(p) && typeof p.id === "string" && Array.isArray(p.indicators)),
        }
        : { panes: [] };
    const createdAt = typeof parsed.createdAt === "number" && Number.isFinite(parsed.createdAt) ? parsed.createdAt : Date.now();
    const updatedAt = typeof parsed.updatedAt === "number" && Number.isFinite(parsed.updatedAt) ? parsed.updatedAt : createdAt;
    return {
        id: parsed.id,
        name: parsed.name,
        drawings,
        indicators,
        layout,
        createdAt,
        updatedAt,
    };
}
export function createRESTStorageAdapter(apiBaseUrl, options) {
    return {
        async save(symbol, timeframe, drawings) {
            const headers = { "Content-Type": "application/json" };
            if (options?.authToken) {
                headers["Authorization"] = `Bearer ${options.authToken}`;
            }
            const response = await fetchWithStorageTimeout(`${apiBaseUrl}/api/drawings`, {
                method: "POST",
                headers,
                body: JSON.stringify({ symbol, timeframe, drawings }),
            }, "save");
            if (!response.ok) {
                throw new Error(`Failed to save drawings to REST API: ${response.status}`);
            }
        },
        async load(symbol, timeframe) {
            const headers = {};
            if (options?.authToken) {
                headers["Authorization"] = `Bearer ${options.authToken}`;
            }
            const response = await fetchWithStorageTimeout(`${apiBaseUrl}/api/drawings?symbol=${encodeURIComponent(symbol)}&timeframe=${encodeURIComponent(timeframe)}`, {
                headers,
            }, "load");
            if (!response.ok) {
                throw new Error(`Failed to load drawings from REST API: ${response.status}`);
            }
            const data = await response.json();
            if (!data || !Array.isArray(data.drawings)) {
                return [];
            }
            return normalizeDrawings(data.drawings);
        },
        async clear(symbol, timeframe) {
            const headers = {};
            if (options?.authToken) {
                headers["Authorization"] = `Bearer ${options.authToken}`;
            }
            const response = await fetchWithStorageTimeout(`${apiBaseUrl}/api/drawings?symbol=${encodeURIComponent(symbol)}&timeframe=${encodeURIComponent(timeframe)}`, {
                method: "DELETE",
                headers,
            }, "clear");
            if (!response.ok) {
                throw new Error(`Failed to clear drawings from REST API: ${response.status}`);
            }
        },
        exportJSON(drawings) {
            return exportDrawingsJSON(drawings);
        },
        importJSON(payload) {
            try {
                return normalizeDrawings(importDrawingsJSON(payload));
            }
            catch (error) {
                if (error instanceof DrawingImportError) {
                    throw error;
                }
                throw new DrawingImportError(error instanceof Error ? error.message : "Invalid drawing JSON payload");
            }
        },
        // P1/D-09: `saveAllForSymbol` removed (dual-writer). `loadAllForSymbol`
        // stays as a legacy read-cache for REST/backward compatibility.
        async loadAllForSymbol(symbol) {
            const headers = {};
            if (options?.authToken) {
                headers["Authorization"] = `Bearer ${options.authToken}`;
            }
            const response = await fetch(`${apiBaseUrl}/api/drawings/symbol/${encodeURIComponent(symbol)}`, {
                headers,
            });
            if (!response.ok) {
                throw new Error(`Failed to load all drawings for symbol from REST API: ${response.status}`);
            }
            const data = await response.json();
            if (!data || !Array.isArray(data.drawings)) {
                return [];
            }
            return normalizeDrawings(data.drawings);
        },
        async clearAllForSymbol(symbol) {
            const headers = {};
            if (options?.authToken) {
                headers["Authorization"] = `Bearer ${options.authToken}`;
            }
            const response = await fetch(`${apiBaseUrl}/api/drawings/symbol/${encodeURIComponent(symbol)}`, {
                method: "DELETE",
                headers,
            });
            if (!response.ok) {
                throw new Error(`Failed to clear all drawings for symbol from REST API: ${response.status}`);
            }
        },
        // Phase 4.1: ChartTemplate persistence
        async saveTemplate(template) {
            const headers = { "Content-Type": "application/json" };
            if (options?.authToken) {
                headers["Authorization"] = `Bearer ${options.authToken}`;
            }
            const response = await fetch(`${apiBaseUrl}/api/templates`, {
                method: "POST",
                headers,
                body: JSON.stringify(template),
            });
            if (!response.ok) {
                throw new Error(`Failed to save chart template to REST API: ${response.status}`);
            }
        },
        async loadTemplate(templateId) {
            const headers = {};
            if (options?.authToken) {
                headers["Authorization"] = `Bearer ${options.authToken}`;
            }
            const response = await fetch(`${apiBaseUrl}/api/templates/${encodeURIComponent(templateId)}`, {
                headers,
            });
            if (!response.ok) {
                return null;
            }
            const data = await response.json();
            if (!data || typeof data.id !== "string" || typeof data.name !== "string") {
                return null;
            }
            try {
                return deserializeChartTemplate(JSON.stringify(data));
            }
            catch {
                return null;
            }
        },
        async listTemplates() {
            const headers = {};
            if (options?.authToken) {
                headers["Authorization"] = `Bearer ${options.authToken}`;
            }
            const response = await fetch(`${apiBaseUrl}/api/templates`, {
                headers,
            });
            if (!response.ok) {
                return [];
            }
            const data = await response.json();
            if (!data || !Array.isArray(data.templates)) {
                return [];
            }
            return data.templates.filter((id) => typeof id === "string");
        },
        async deleteTemplate(templateId) {
            const headers = {};
            if (options?.authToken) {
                headers["Authorization"] = `Bearer ${options.authToken}`;
            }
            const response = await fetch(`${apiBaseUrl}/api/templates/${encodeURIComponent(templateId)}`, {
                method: "DELETE",
                headers,
            });
            if (!response.ok) {
                throw new Error(`Failed to delete chart template from REST API: ${response.status}`);
            }
        },
    };
}
